import React from "react";
import { Flex } from "@chakra-ui/react";
import { format, parse } from "date-fns";
import { AccordionItem } from "components/index";
import {
  MedicationCalculationTypeEnum,
  RegionalBlockTypeEnum,
  RouteEnum
} from "requests";
import { CardField } from "./components";
import { constants } from "./duck";

interface CardProps {
  id: number | string;
  name: string;
  data: any;
  isSelected?: boolean;
  onSelect?(): void;
  allowToggle?: boolean;
  expanded?: boolean;
  doseUnit?: string;
  viewOnly?: boolean;
  hasTimeAdministered: boolean;
  hasAdditionalVolumeToBeDrawn: boolean;
  medicationType:
    | MedicationCalculationTypeEnum
    | RegionalBlockTypeEnum
    | "tablet"
    | "suspension"
    | "defib"
    | "fluidBolus";
}

const Card: React.FC<CardProps> = ({
  id,
  name,
  data,
  isSelected,
  onSelect,
  allowToggle = true,
  expanded = true,
  doseUnit = "mg",
  viewOnly = false,
  medicationType,
  hasTimeAdministered,
  hasAdditionalVolumeToBeDrawn
}) => {
  const time = data?.timeAdministered
    ? parse(data.timeAdministered, "HH:mm:ssxxx", new Date())
    : null;
  const formattedTime = time ? format(time, "h:mm aaaa") : null;

  const unit = name === "Vasopressin" ? "U" : doseUnit;

  return (
    <AccordionItem
      defaultExpanded={expanded}
      title={name}
      allowToggle={allowToggle}
      isSelected={isSelected}
      onSelect={onSelect}
      variant="gray"
      collapsedExtra={[
        data?.dose != null && `${data.dose} ${unit}/kg`,
        data?.doseMin != null &&
          data?.doseMax != null &&
          `${data.doseMin} to ${data.doseMax} ${unit}/kg`,
        data?.dosageMin != null &&
          data?.dosageMax != null &&
          `${+data.dosageMin.toFixed(3)} to ${+data.dosageMax.toFixed(
            3
          )} ${unit}`,
        data?.totalVolume != null && `${+data.totalVolume.toFixed(3)} mL`,
        data?.route && constants.ROUTES_LABELS[data.route as RouteEnum],
        formattedTime,
        name === "Fluid Bolus" &&
          data?.totalAmount != null &&
          `${data.totalAmount} mLs`,
        data?.volumeToBeInfused != null && `${data.volumeToBeInfused} ml`
      ].filter(Boolean)}
    >
      <Flex direction="column">
        <Flex flex={1} wrap="wrap" gap={6}>
          {data?.dose != null && (
            <CardField
              medicationId={id}
              name="dose"
              label={`Dose (${unit}/kg)`}
              value={data.dose}
              viewOnly={viewOnly}
            />
          )}
          {data?.doseMin != null && (
            <CardField
              medicationId={id}
              name="doseMin"
              label={`Min Dose (${unit}/kg)`}
              value={data.doseMin}
              viewOnly={viewOnly}
            />
          )}
          {data?.doseMax != null && (
            <CardField
              medicationId={id}
              name="doseMax"
              label={`Max Dose (${unit}/kg)`}
              value={data.doseMax}
              viewOnly={viewOnly}
            />
          )}
          {data?.dosage != null && (
            <CardField
              medicationId={id}
              name="dosage"
              label="Dosage"
              value={+data.dosage.toFixed(3)}
              viewValue={`${+data.dosage.toFixed(3)} ${unit}`}
              viewOnly={viewOnly}
            />
          )}
          {data?.dosageMin != null && (
            <CardField
              medicationId={id}
              name="dosageMin"
              label={`Min Dosage (${unit})`}
              viewValue={`${+data.dosageMin.toFixed(3)} ${unit}`}
              value={+data.dosageMin.toFixed(3)}
              viewOnly={viewOnly}
            />
          )}
          {data?.dosageMax != null && (
            <CardField
              medicationId={id}
              name="dosageMax"
              label={`Max Dosage (${unit})`}
              viewValue={`${+data.dosageMax.toFixed(3)} ${unit}`}
              value={+data.dosageMax.toFixed(3)}
              viewOnly={viewOnly}
            />
          )}
          {data?.concentration != null && (
            <CardField
              medicationId={id}
              name="concentration"
              label={`Concentration (${unit}/mL)`}
              value={data.concentration}
              viewOnly={viewOnly}
            />
          )}
          {data?.totalAmount != null && (
            <CardField
              medicationId={id}
              name="totalAmount"
              label={`Total Amount (${unit})`}
              viewValue={`${+data.totalAmount.toFixed(3)} ${unit}`}
              value={+data.totalAmount.toFixed(3)}
              viewOnly={viewOnly}
            />
          )}
          {data?.route && (
            <CardField
              medicationId={id}
              name="route"
              label="Route"
              viewValue={constants.ROUTES_LABELS[data.route as RouteEnum]}
              value={data.route}
              viewOnly={viewOnly}
            />
          )}
          {hasTimeAdministered &&
            medicationType !== MedicationCalculationTypeEnum.Induction &&
            medicationType !== RegionalBlockTypeEnum.RegionalOralBlocks &&
            medicationType !== RegionalBlockTypeEnum.EpiduralCalculations &&
            medicationType !== "tablet" &&
            medicationType !== "suspension" &&
            medicationType !== "fluidBolus" && (
              <CardField
                medicationId={id}
                name="timeAdministered"
                label="Time Administered"
                value={formattedTime}
                viewOnly={viewOnly}
              />
            )}
          {data?.volumeRangeMin != null && (
            <CardField
              medicationId={id}
              name="volumeRangeMin"
              label="Min Volume (mLs)"
              viewValue={`${+data.volumeRangeMin.toFixed(3)} mLs`}
              value={+data.volumeRangeMin.toFixed(3)}
              viewOnly={viewOnly}
            />
          )}
          {data?.volumeRangeMax != null && (
            <CardField
              medicationId={id}
              name="volumeRangeMax"
              label="Max Volume (mLs)"
              viewValue={`${+data.volumeRangeMax.toFixed(3)} mLs`}
              value={+data.volumeRangeMax.toFixed(3)}
              viewOnly={viewOnly}
            />
          )}
          {(medicationType === RegionalBlockTypeEnum.RegionalOralBlocks ||
            medicationType === RegionalBlockTypeEnum.EpiduralCalculations) && (
            <CardField
              medicationId={id}
              name="volumeToBeInfused"
              label="Volume to be infused"
              viewValue={
                data.volumeToBeInfused != null
                  ? `${data.volumeToBeInfused} ml`
                  : "-"
              }
              value={data.volumeToBeInfused}
              viewOnly={viewOnly}
            />
          )}
          {data?.volume != null && (
            <CardField
              medicationId={id}
              name="volume"
              label="Volume"
              viewValue={`${+data.volume.toFixed(3)} mLs`}
              value={+data.volume.toFixed(3)}
              viewOnly={viewOnly}
            />
          )}
          {medicationType === "tablet" && (
            <CardField
              medicationId={id}
              name="tabletSize"
              label="Tablet Size"
              value={data.tabletSize}
              viewOnly={viewOnly}
            />
          )}
          {medicationType === "tablet" && (
            <CardField
              medicationId={id}
              name="numberOfTablets"
              label="Number of Tablets to go Home"
              value={data.numberOfTablets}
              viewOnly={viewOnly}
            />
          )}
          {medicationType !== MedicationCalculationTypeEnum.Induction &&
            (data?.totalVolume != null || medicationType === "suspension") && (
              <CardField
                medicationId={id}
                name="totalVolume"
                label="Total Volume (mL)"
                value={
                  data.totalVolume != null ? +data.totalVolume.toFixed(3) : null
                }
                viewValue={
                  data.totalVolume != null
                    ? `${+data.totalVolume.toFixed(3)} mL`
                    : "-"
                }
                important={medicationType !== "suspension"}
                viewOnly={viewOnly}
              />
            )}
          {medicationType !== MedicationCalculationTypeEnum.Induction &&
            data.initials !== undefined && (
              <CardField
                medicationId={id}
                name="initials"
                label="Initials"
                value={data.initials || ""}
                viewOnly={viewOnly}
                textHeight="initial"
              />
            )}
          {(medicationType === "tablet" || medicationType === "suspension") && (
            <CardField
              medicationId={id}
              name="labelInstructions"
              label="Label Instructions"
              value={data.labelInstructions}
              viewOnly={viewOnly}
              textHeight="initial"
            />
          )}
        </Flex>

        {medicationType === MedicationCalculationTypeEnum.Induction && (
          <Flex mt={6} flex={1} wrap="wrap" gap={6}>
            {hasAdditionalVolumeToBeDrawn && (
              <CardField
                medicationId={id}
                name="totalVolume"
                label="Total Volume To Be Drawn (mL)"
                value={+data.totalVolume.toFixed(3)}
                viewValue={`${+data.totalVolume.toFixed(3)} mL`}
                important
                viewOnly={viewOnly}
              />
            )}
            <CardField
              medicationId={id}
              name="volumeAdministered"
              label="Volume Administered"
              value={data.volumeAdministered}
              viewOnly={viewOnly}
            />
            {hasTimeAdministered && (
              <CardField
                medicationId={id}
                name="timeAdministered"
                label="Time Administered"
                value={formattedTime}
                viewOnly={viewOnly}
              />
            )}
          </Flex>
        )}

        {medicationType === MedicationCalculationTypeEnum.Induction && (
          <Flex mt={6} flex={1} wrap="wrap" gap={6}>
            <CardField
              medicationId={id}
              name="additionalVolumeDrawn"
              label="Additional Volume Drawn"
              value={data.additionalVolumeDrawn}
              viewOnly={viewOnly}
            />
            <CardField
              medicationId={id}
              name="additionalVolumeAdministered"
              label="Additional Volume Administered"
              value={data.additionalVolumeAdministered}
              viewOnly={viewOnly}
            />
            {data.initials !== undefined && (
              <CardField
                medicationId={id}
                name="initials"
                label="Initials"
                value={data.initials}
                viewOnly={viewOnly}
                textHeight="initial"
              />
            )}
          </Flex>
        )}
      </Flex>
    </AccordionItem>
  );
};

export default Card;
